
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AuthForm } from "./AuthForm";
import { InvenioLogo } from "@/components/ui/invenio-logo";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AuthCardProps {
  onSubmit: (values: any) => void;
  isLoading: boolean;
  authMode: "login" | "signup" | "forgotPassword";
  setAuthMode: (mode: "login" | "signup" | "forgotPassword") => void;
  authError?: string | null;
}

export const AuthCard = ({ onSubmit, isLoading, authMode, setAuthMode, authError }: AuthCardProps) => {
  const handleTabChange = (value: string) => {
    if (value === "login" || value === "signup") {
      setAuthMode(value);
    }
  };
  
  return (
    <Card className="w-full max-w-md shadow-2xl backdrop-blur-sm bg-white/95 animate-fade-in">
      <CardHeader className="space-y-4 items-center text-center pb-6">
        <div className="flex flex-col items-center gap-3">
          <InvenioLogo size="lg" className="text-invenio-primary" />
          <h1 className="text-2xl font-semibold text-invenio-secondary tracking-tight">
            InvenioCRM
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          {authMode === "login" 
            ? "Welcome back! Sign in to your account" 
            : "Create an account to get started"
          }
        </p>
      </CardHeader>
      <CardContent>
        {authError && !isLoading && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{authError}</AlertDescription>
          </Alert>
        )}
        
        <Tabs 
          value={authMode === "forgotPassword" ? "login" : authMode} 
          onValueChange={handleTabChange}
          className="animate-fade-in"
        >
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>
          
          <AuthForm 
            onSubmit={onSubmit}
            isLoading={isLoading}
            mode={authMode === "forgotPassword" ? "login" : authMode}
          />
          
          {authMode === "login" && (
            <div className="text-center text-sm text-muted-foreground mt-6">
              <Button
                variant="link"
                className="p-0 h-auto font-normal text-muted-foreground hover:text-invenio-primary transition-colors duration-200"
                onClick={() => setAuthMode("forgotPassword")}
              >
                Forgot your password?
              </Button>
            </div>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
};
